import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useEffect, useState } from 'react';
import MoneyLabel from '../label/MoneyLabel';
import { useTranslation } from 'react-i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RenderHTML from '../preview/RenderHTML';
import { numberToString } from './numberConverter';
import EditIcon from '@mui/icons-material/Edit';
import DateLabel from '../label/DateLabel';
import { renderHours } from '../../scenes/time-tracking/worktime';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';

export const BillingPosition = ({
  position,
  onEdit,
  onSelectionChange,
  selection,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);

  const {
    type,
    name,
    description,
    unit,
    amount,
    taxRate,
    price,
    image,
    workPackages,
    totalTimeBilledSeconds,
    totalTimeWorkedSeconds,
  } = position;

  const workPackageCount = workPackages.length;

  const iconMap = {
    time: AccessTimeIcon,
    flat: AttachMoneyIcon,
  };

  const billableSeconds = totalTimeWorkedSeconds - totalTimeBilledSeconds;

  const priceMap = {
    time: (
      <Box>
        <Typography>
          <span style={{ fontSize: 10 }} className="mr-1 opacity-50">
            {t('open')}
          </span>
          <span className="monospace" style={{ fontWeight: 500 }}>
            <MoneyLabel money={(billableSeconds / 60 / 60) * price} />
          </span>
        </Typography>
        <Typography className="opacity-50">
          <MoneyLabel money={price} />
          <span style={{ fontSize: 10 }} className="ml-1 ">
            {`/ ${t('hour')}`}
          </span>
        </Typography>
      </Box>
    ),
    flat: (
      <Box>
        <Typography className="hidden">
          <span style={{ fontSize: 10 }} className="mr-1 opacity-50">
            {t('open')}
          </span>
          <span className="monospace" style={{ fontWeight: 500 }}>
            <MoneyLabel money={(billableSeconds / 60 / 60) * price} />
          </span>
        </Typography>
        <Typography>
          <MoneyLabel money={price} />
          {unit && (
            <span style={{ fontSize: 10 }} className="ml-1 opacity-50">
              {` / ${unit}`}
            </span>
          )}
        </Typography>
      </Box>
    ),
  };

  const Icon = iconMap[type];
  const priceLabel = priceMap[type];

  // select logic
  function countSelectedItems() {
    var selectedCount = 0;
    workPackages.forEach((wp) => {
      if (selection.includes(wp.id)) selectedCount++;
    });
    return selectedCount;
  }

  useEffect(() => {
    if (selection) setSelectedCount(countSelectedItems());
  }, [selection]);

  const SelectedIcon =
    selectedCount === workPackageCount
      ? CheckBoxIcon //selected
      : selectedCount === 0
      ? CheckBoxOutlineBlankIcon //nothing selected
      : IndeterminateCheckBoxIcon;

  function handleSelectedWorkPackage(wp) {
    if (selection.includes(wp.id)) {
      onSelectionChange(selection.filter((wpId) => wpId !== wp.id));
    } else {
      onSelectionChange([...selection, wp.id]);
    }
  }

  function handleMainSelect(e) {
    e.stopPropagation();
    // remove all items from selection
    if (selectedCount === workPackageCount) {
      const idsToRemove = new Set(workPackages.map((obj) => obj.id));
      return onSelectionChange(selection.filter((id) => !idsToRemove.has(id)));
    }

    // add all items to selection
    const idsToAdd = workPackages.map((obj) => obj.id);
    // merge old selection with new ids, avoid duplicates using Set
    const newSelection = Array.from(new Set([...selection, ...idsToAdd]));
    return onSelectionChange(newSelection);
  }

  return (
    <Box
      sx={{
        bgcolor: colors.bgInput,
        border: `1px solid ${colors.selected}`,
        '.edit-button': {
          opacity: 0,
          transform: 'scale(0)',
          transition: '0.3s ease',
        },
        ':hover .edit-button': {
          opacity: 1,
          transform: 'scale(1)',
        },
      }}
      className="flex flex-col w-full rounded-lg"
    >
      <Button
        onClick={() => setOpen(!open)}
        className="px-2 py-2 flex items-center gap-2 min-h-[56px]"
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Box className="flex items-center gap-2">
          {onSelectionChange && (
            <IconButton onClick={handleMainSelect}>
              {SelectedIcon && <SelectedIcon />}
            </IconButton>
          )}
          {image ? (
            <img
              className="w-[40px] h-[40px] rounded-md object-cover"
              src={image.temporaryUrl}
            />
          ) : (
            <Icon />
          )}

          <Typography fontWeight={500}>
            {name}
            <span className="opacity-50 ml-2" style={{ fontSize: 10 }}>
              {workPackageCount > 0
                ? `[${workPackageCount} ${
                    workPackageCount === 1
                      ? t('Work Package')
                      : t('Work Packages')
                  }]`
                : t('No Work Package')}
            </span>
          </Typography>
          {onEdit && (
            <IconButton
              onClick={() => onEdit(position)}
              className="edit-button"
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <Box className="flex gap-1">
          {priceLabel}
          <ArrowDropDownIcon
            sx={{ transform: `rotate(${open ? '180deg' : '0deg'})` }}
          />
        </Box>
      </Button>
      <Collapse in={open}>
        <Box className="flex flex-col px-4 pb-2">
          <Box className="flex gap-4 flex-row-reverse">
            {amount && (
              <Box>
                <Typography fontSize={10} className="opacity-50">
                  {t('Amount')}
                </Typography>
                <Typography className="monospace">
                  {`${numberToString(amount, 2)} ${unit || ''}`}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography fontSize={10} className="opacity-50">
                {t('Tax Rate')}
              </Typography>
              <Typography className="monospace">
                {numberToString(taxRate, 0)} %
              </Typography>
            </Box>
          </Box>
          {description && (
            <Box>
              <Typography fontSize={10} className="opacity-50">
                {t('Description')}
              </Typography>
              <RenderHTML HTML={description} />
            </Box>
          )}
          {workPackageCount > 0 && (
            <Box className="flex flex-col w-full gap-2">
              <Typography fontSize={10} className="opacity-50">
                {t('Work Packages')}
              </Typography>
              {workPackages.map((wp) => (
                <WPPreview
                  workPackage={wp}
                  onSelect={
                    onSelectionChange ? handleSelectedWorkPackage : null
                  }
                  isSelected={selection?.includes(wp.id)}
                />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const WPPreview = ({ workPackage, onSelect, isSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();
  const { title, nr, estimatedTime, startDate, endDate, isBillable, icon } =
    workPackage;

  if (!Boolean(isBillable)) return;

  const SelectedIcon = isSelected
    ? CheckBoxIcon //selected
    : CheckBoxOutlineBlankIcon; //nothing selected

  return (
    <Box
      className="rounded-md px-2 py-1 flex justify-between cursor-pointer"
      sx={{
        border: `1px solid ${
          isSelected ? `${colors.info}80` : colors.selected
        }`,
        bgcolor: isSelected ? colors.info + '30' : 'transparent',
        ':hover': onSelect
          ? {
              border: `1px solid ${colors.info}80`,
              bgcolor: colors.info + '30',
            }
          : {},
      }}
      onClick={onSelect ? () => onSelect(workPackage) : null}
    >
      <Box className="flex gap-2 items-center">
        {onSelect && <SelectedIcon />}
        {icon && (
          <img
            src={icon?.temporaryUrl}
            className="w-[36px] h-[36px] object-contain"
          />
        )}
        <Box className="flex flex-col">
          <Typography>
            <span className="monospace mr-2 opacity-50">{nr}</span>
            {title}
          </Typography>
          <Typography fontSize={10} className="opacity-50">
            <DateLabel date={startDate} />
            {` ${t('until')} `}
            <DateLabel date={endDate} />
          </Typography>
        </Box>
      </Box>
      <Typography className="monospace">
        {`${renderHours(workPackage.totalTimeWorkedSeconds / 60)} h`}
        {estimatedTime && (
          <span className="ml-2 opacity-50" style={{ fontSize: 10 }}>
            {`/ ${renderHours(estimatedTime / 60)} h`}
          </span>
        )}
      </Typography>
    </Box>
  );
};
